import React, { useEffect, useState, useRef } from 'react';
import { API, Auth } from 'aws-amplify';
import { AmplifyUser } from '@aws-amplify/ui';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './SaggitaView.css';
import 'chartjs-adapter-moment';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

type HomeProps = {
  activeLink: VoidFunction,
  user: AmplifyUser
};

/** 対象Saggita */
type SaggitaType = [{
  saggitaId: string,
  name: string
}];

const initSaggitaPulldown = {
  saggitaId: "",
  name: ""
}

/** 最新データ一覧 */
type LatestDataListType = [{
  channel: string,
  name: string,
  num: string,
  unit: string,
  batteryValue: string,
  recvDatetime: string
}];

const initLatestDataList = {
  channel: "",
  name: "",
  num: "",
  unit: "",
  batteryValue: "",
  recvDatetime: ""
}

/** グラフ */
type GraphDataDataType = {
  labels: string[],
    datasets: [
      {
        label: string,
        data: number[],
        borderColor: string,
        borderWidth: number
      }
    ]
}

type GraphDataOptionType = {
  responsive: boolean,
  plugins:{
    title: {
      display: boolean,
      text: string,
      fontSize: number
    }
  },
  maintainAspectRatio: boolean
}

type GraphDataType = {
  data: GraphDataDataType,
  options: GraphDataOptionType
}

const initGraphData: GraphDataType = {
  data: {
    labels: [""],
    datasets: [
      {
        label: "",
        data: [0],
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 1
      }
      ]
  },
  options: {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: "",
        fontSize: 16
      }
    },
    maintainAspectRatio: false
  }  
};

type GraphResponseType = {
  label: string,
  data: [
    {
      num: number,
      recvDatetime: string
    }
  ]
}

type urlProps = {
  id: string
}

const AlertMessage = {
  SV01001: { id: "SV01001", msg: "参照可能なSaggitaが登録されていません。" },
  SV01002: { id: "SV01002", msg: "対象Saggitaの取得に失敗しました。"},
  SV01003: { id: "SV01003", msg: "対象Saggitaデータの取得に失敗しました。" },
  SV01004: { id: "SV01004", msg: "対象SaggitaデータのCSV出力に失敗しました。"}
}

const API_NAME = 'MainApi'
const API_PATH_PREFIX = '/saggita'

const SaggitaView: React.FC<HomeProps> = (props) => {
  // 画面初期化

  // const [searchParams] = useSearchParams();
  // const queryId = searchParams.get("id");
  const urlParams = useParams<urlProps>();
  const queryId = urlParams.id;

  // 対象Saggita
  const [saggitaPulldown, setSaggitaPulldown] = useState<SaggitaType>([initSaggitaPulldown]);
  const [selectedSaggitaId, setSelecttedSaggitaId] = useState("");
  const [pulldownDisabled, setPulldownDisabled] = useState<boolean>(true);
  const [pulldownClassName, setPulldownClassName] = useState("form-control text-black-50");
  // CSVボタン
  const [csvBtnDisable, setCsvBtnDisable] = useState<boolean>(true);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  // 最新データ一覧
  const [listClass, setListClass] = React.useState('d-none');
  const [latestData, setLatestData] = useState<LatestDataListType>([initLatestDataList]);
  const [listLoading, setListLoading] = useState(false);
  // グラフ
  const [graphDivClass, setGraphDivClass] = React.useState('d-none');
  const [graphData, setGraphData] = React.useState<any[]>([initGraphData]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [alertText, setAlertText] = React.useState('');
  const [alertClass, setAlertClass] = React.useState('alert alert-danger d-none');

  const [anchorHref, setAnchorHref] = useState("/")
  const hiddenAnchor = useRef<HTMLAnchorElement>(null);

  props.activeLink();
  const userId = props.user.username

  let loadFlg = false;
  useEffect(() => {
    // 対象Saggita取得
    if (!loadFlg) {
      loadFlg = true;
      getSaggitaPulldown();
    }

    // ウィンドウサイズ横幅取得
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };

  }, []);

  const graphDivStyle : React.CSSProperties = {
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: "10px",
    // 「915」がGoogle Pixel 6aの横持ち時のウィンドウサイズ
    width: windowWidth <= 1000 ? 
      "1000px" // スマホ or ウィンドウサイズを小さくした時
      : "100%", // PC
    height: "300px"
  }

  const getSaggitaPulldown = async () => {
    try {
      const auth = {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
        }
      };
      const data = await API.get(API_NAME, API_PATH_PREFIX + '/' + userId, auth);
      if(data.length == 0) {
          // setAlertMessage(AlertMessage.SV01001);
          setSaggitaPulldown(data);
          setPulldownDisabled(false);
          return;
      }
      setSaggitaPulldown(data);
      setPulldownDisabled(false);

      if (queryId !== undefined) {
        // setSelecttedSaggitaId(queryId);
        viewSaggita(queryId);
      }

    } catch (err) {
      console.log(err);
      setAlertMessage(AlertMessage.SV01002);
    }
  };

  // 対象Saggita変更
  const onChangeSaggita = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    console.log("[onChangeSaggita] saggitaId: " + e.target.value);
    if (e.target.value == "-1") {
      hiddenAnchor.current?.setAttribute("href", "/");
      hiddenAnchor.current?.click();
    }
    else {
      hiddenAnchor.current?.setAttribute("href", `/${e.target.value}`);
      hiddenAnchor.current?.click();
    }
  }

  // Saggita変更に伴い、表とグラフを更新
  const viewSaggita = async (id: string) => {
    console.log("[viewSaggita] saggitaId: " + id);
      try {
      setAlertClass('alert alert-danger d-none');
      setListClass("d-none");
      setGraphDivClass("d-none");
      setPulldownDisabled(true);
      setCsvBtnDisable(true);
      let saggitaId = id;
      setSelecttedSaggitaId(saggitaId);
      if (saggitaId == "-1") {
        setCsvBtnDisable(true);
        setPulldownDisabled(false);
        setPulldownClassName("form-control text-black-50");
        return;
      }
      setPulldownClassName("form-control text-body");
      const auth = {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
        }
      };
      const listApiCall = async() => {
        // 一覧データ取得API
        setListLoading(true);
        const listData = await API.get(API_NAME, API_PATH_PREFIX + "/view/" + saggitaId, auth);
        setLatestData(listData);
        setListClass("mb-5");
        setListLoading(false);
      };
      const grapApiCall = async() => {
        // グラフデータ取得API
        setGraphLoading(true);
        const graphDataRes = await API.get(API_NAME, API_PATH_PREFIX + "/graph/" + saggitaId, auth);
        const graphData: any[] = [];
        graphDataRes.forEach((item: GraphResponseType) => {
          let minRecvDatetime = "";
          let maxRecvDatetime = "";
          // data
          const labels: string[] = []
          const label: string = item.label;
          const nums: number[] = []
          item.data.forEach((item, index) => {
            labels.push(item.recvDatetime);
            nums.push(item.num);
            if (index === 0) {
              minRecvDatetime = item.recvDatetime;
            }
            else {
              maxRecvDatetime = item.recvDatetime;
            }
          });
          let minDate = new Date(minRecvDatetime);
          let maxDate = new Date(maxRecvDatetime);
          let labelMinDate = new Date(
            minDate.getFullYear(),
            minDate.getMonth(),
            minDate.getDate(),
            9,
            0,
            0);
          const data: GraphDataDataType = {
            labels: labels,
            datasets: [
              {
                label: "",
                data: nums,
                borderColor: "rgb(75, 192, 192)",
                borderWidth: 1
              }
            ]
          }
          // options
          const options = {
            responsive: true,
            plugins: {
              title: {
                display: true,
                // text: "数値[" + item.label + "]",
                text: item.label,
                fontSize: 16
              },
            },
            maintainAspectRatio: false,
            scales:{
              x: {
                distribution: 'liner',
                type: 'time' as const,
                time: {
                  unit: 'hour' as const,
                  displayFormats: {
                    hour: 'YYYY/MM/DD HH:mm'
                  } as const,
                } as const
              }
            }
          }
          graphData.push({
            data: data,
            options: options
          })
        });
        setGraphData(graphData);
        setGraphDivClass("overflow-auto");
        setGraphLoading(false);
      }
      // APIを同時に呼び出す
      await Promise.all([listApiCall(), grapApiCall()]);

      // 2つのAPIの処理完了後以下の処理を実施
      setCsvBtnDisable(false);
      setPulldownDisabled(false);

    } catch (err) {
      console.log(err);
      setAlertMessage(AlertMessage.SV01003);
      setCsvBtnDisable(false);
      setPulldownDisabled(false);
      setListLoading(false);
      setGraphLoading(false);
    } 
  }

  // CSVボタンクリック
  const onClickExportButton = async () => {
    try {
      setAlertClass('alert alert-danger d-none');
      let saggitaId = selectedSaggitaId;
      if (selectedSaggitaId == "-1") {
        return;
      }
      setCsvBtnDisable(true);
      setPulldownDisabled(true);
      setCsvLoading(true);
      const auth = {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
        }
      };
      let response = await API.get(API_NAME, API_PATH_PREFIX + "/export/" + saggitaId, auth);
      let date = new Date();
      let year = date.getFullYear();
      let month = ("0" + (date.getMonth() + 1)).slice(-2);
      let day = ("0" + date.getDate()).slice(-2);
      let hours = ("0" + date.getHours()).slice(-2);
      let minutes = ("0" + date.getMinutes()).slice(-2);
      let seconds = ("0" + date.getSeconds()).slice(-2);
      let fileName = `リモート電防モニタ NAKA-NOSU-KE_${year}${month}${day}${hours}${minutes}${seconds}.csv`
      let bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
      let url = window.URL.createObjectURL(new Blob([bom, response]));
      let link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setCsvBtnDisable(false);
      setPulldownDisabled(false);
      setCsvLoading(false);
    } catch (err) {
      console.log(err);
      setAlertMessage(AlertMessage.SV01004);
      setCsvBtnDisable(false);
      setPulldownDisabled(false);
      setCsvLoading(false);
    } 
  }

  const setAlertMessage = (type : any) => {
    setAlertText(type.id + ' ' + type.msg);
    setAlertClass('alert alert-danger');
  };

  return (
    <div className="p-3">
      <div className={alertClass} role="alert">
        <h5 className="alert-heading">{alertText}</h5>
      </div>
      <select className={pulldownClassName} value={selectedSaggitaId} disabled={pulldownDisabled} onChange={(e) => onChangeSaggita(e)}>
          <option key={"saggitaop-00"} value="-1" className='text-black-50'>▽機体名称を選択▽</option>
          {
            saggitaPulldown.map((data, index) => (
              <option key={"saggitaop-" + index} value={data.saggitaId} className='text-body'>{data.saggitaId + " " + data.name}</option>
            ))
          }
      </select>
      <div className="mt-3 align-items-center d-flex">
        <button className="btn btn-primary col-12" id="exportBtn" onClick={(e) => onClickExportButton()} disabled={csvBtnDisable}>
          CSV {csvLoading ? <div className="spinner-border text-white spinner-border-sm" role="status"></div> : null}
        </button>
      </div>      
      <div ></div>
      <div className="mt-5">
        <h6>最新データ</h6>
        {
          listLoading && (
            <Spinner animation="border" role="status" className="text-primary">
              <span className="sr-only">Loading...</span>
            </Spinner>
          )
        }
      </div>
      <div className={listClass}>
        <div className="table-responsive">
          <table className="table text-nowrap border">
            <thead>
              <tr>
                <th scope="col">CH</th>
                <th scope="col">CH名称</th>
                <th scope="col">数値</th>
                <th scope="col">単位</th>
                <th scope="col">電池電圧[V]</th>
                <th scope="col">最終測定日時</th>
              </tr>
            </thead>
            <tbody>
              {
                latestData.map((data, index) => (
                  <tr key={"tr-" + index}>
                    <td className={data.num == "無効" ? "gray-out" : ""} key={"tdchannel-" + index}>{data.channel}</td>
                    <td className={data.num == "無効" ? "gray-out" : ""} key={"tdname-" + index}>{data.name}</td>
                    <td className={data.num == "無効" ? "gray-out" : ""} key={"tdnum-" + index}>{data.num}</td>
                    <td className={data.num == "無効" ? "gray-out" : ""} key={"tdunit-" + index}>{data.unit}</td>
                    <td key={"tdbatteryValue-" + index}>{data.batteryValue}</td>
                    <td key={"tdrecvDatetime-" + index}>{data.recvDatetime}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-3"></div>
      <h6>グラフ</h6>
      <div>
        {
          graphLoading && (
            <Spinner animation="border" role="status" className="text-primary">
              <span className="sr-only">Loading...</span>
            </Spinner>
          )
        }
      </div>
      <div className={graphDivClass}>
        {
          graphData.map((data, index) => (
            <div key={"divgraph-" + index} className="border" style={graphDivStyle}>
              <Line
                data={data.data}
                options={data.options}
                />
            </div>
          ))
        }
      </div>
      <a className="d-none" ref={hiddenAnchor} href={anchorHref}></a>
    </div>
  );
}

export default SaggitaView;
